// ── Diagrams collection (/blog/diagrams) ────────────────────────────────────
//
// 37 posts ship a hand-built interactive explainer with a click-to-expand
// lightbox. Until now they were injected mid-article with nothing anywhere
// else pointing at them — no badge on the card, no entry in the table of
// contents, no index. They are the one thing on this blog that competitors do
// not have, so they get a room of their own.

import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { DIAGRAM_SLUGS, diagramAnchor } from "@/components/blog-diagrams/index";
import { longReadLabel, postExcerpt, stripTitleSuffix } from "@/lib/blogText";
import { POSTS, getCategoryBySlug } from "@/lib/blogMeta.generated";
import { SITE_URL } from "@/lib/brand";
import CoverImage from "@/components/CoverImage";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

export default function BlogDiagrams() {
  const isMobile = useIsMobile();

  // Registry order is roughly the order they were built; newest first reads
  // better as a collection.
  const bySlug = new Map(POSTS.map((p) => [p.slug, p]));
  const posts = DIAGRAM_SLUGS.map((slug) => bySlug.get(slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

  useSeoMeta({
    title: "Interactive SEO Diagrams | Indexed",
    description:
      "Interactive explainers for how search and AI answers actually work — crawling, citation, authority and ranking, drawn rather than described.",
    canonical: "/blog/diagrams",
    jsonLd: [
      {
        "@type": "CollectionPage",
        "name": "Interactive SEO Diagrams",
        "url": `${SITE_URL}/blog/diagrams`,
        "description": "Interactive explainers from the Indexed blog.",
        "mainEntity": {
          "@type": "ItemList",
          "numberOfItems": posts.length,
          "itemListElement": posts.map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": stripTitleSuffix(p.title),
            "url": `${SITE_URL}/blog/${p.slug}#${diagramAnchor(p.slug)}`,
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
          { "@type": "ListItem", "position": 3, "name": "Diagrams", "item": `${SITE_URL}/blog/diagrams` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div style={{ background: "#0a0c0c", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: isMobile ? "104px 20px 80px" : "132px 48px 120px" }}>

          <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <Link href="/blog" style={{ color: "rgba(255,255,255,0.62)", fontSize: "14px", fontFamily: ff, textDecoration: "none" }}>Blog</Link>
            <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.35)", fontSize: "14px" }}>›</span>
            <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "14px", fontFamily: ff }}>Diagrams</span>
          </nav>

          <h1 style={{
            color: "white", fontSize: "clamp(36px, 4vw, 60px)", fontWeight: 800, fontFamily: ff,
            margin: "0 0 8px", lineHeight: 1.05, letterSpacing: "-0.025em", textWrap: "balance",
          }}>
            Diagrams
          </h1>
          <p style={{ color: "rgba(255,255,255,0.62)", fontSize: "15px", fontFamily: ff, margin: "0 0 20px" }}>
            {posts.length} interactive explainer{posts.length === 1 ? "" : "s"}
          </p>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "17px", fontFamily: ff, lineHeight: 1.7, margin: "0 0 48px", maxWidth: "70ch" }}>
            How search and AI answers actually work — crawling and indexation, how engines choose what to cite,
            where authority comes from, what moves a ranking. Each one is built for its article, and each one opens
            full-screen. Click any diagram to expand it.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "20px" }}>
            {posts.map((post) => {
              const cat = getCategoryBySlug(post.categories[0]);
              const summary = postExcerpt(post);
              return (
                <Link
                  key={post.slug}
                  href={`/blog/${post.slug}#${diagramAnchor(post.slug)}`}
                  className="diagram-card"
                  style={{ display: "flex", flexDirection: "column", textDecoration: "none", height: "100%" }}
                >
                  <div style={{ height: "180px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
                    <CoverImage post={post} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    <span className="interactive-badge">Interactive</span>
                  </div>
                  <div style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                    {cat && (
                      <span style={{
                        fontSize: "10px", fontWeight: 800, fontFamily: ff, letterSpacing: "0.09em",
                        textTransform: "uppercase", color: cat.color,
                      }}>{cat.label}</span>
                    )}
                    <h2 style={{
                      color: "white", fontSize: "17px", fontWeight: 700, fontFamily: ff,
                      margin: 0, lineHeight: 1.35, letterSpacing: "-0.01em",
                    }}>{stripTitleSuffix(post.title)}</h2>
                    {summary && (
                      <p style={{
                        color: "rgba(255,255,255,0.6)", fontSize: "14px", fontFamily: ff,
                        lineHeight: 1.55, margin: 0, flex: 1,
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" as const, overflow: "hidden",
                      }}>{summary}</p>
                    )}
                    <span style={{
                      marginTop: "auto", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.06)",
                      color: "rgba(255,255,255,0.62)", fontSize: "13px", fontFamily: ff,
                    }}>
                      {longReadLabel(post.readingTime) || `${post.readingTime} min read`}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <style>{`
          .diagram-card {
            background:#111;
            border:1px solid rgba(255,255,255,0.07);
            border-radius:16px;
            overflow:hidden;
            transition:border-color 0.2s, transform 0.2s;
          }
          .diagram-card:hover { border-color:rgba(87,215,226,0.35); transform:translateY(-3px); }
        `}</style>
      </div>
    </SiteLayout>
  );
}
